<template>
  <!--
    DrawingToolbar
    -------------------------------------------------------------------------
    Barra de ferramentas do desenho. Cada botão apenas EMITE intenção; toda a
    lógica fica no StoneTechnicalDrawing2D. Assim, adicionar uma ferramenta no
    futuro = adicionar um item em `tools` (abaixo) + tratar o `tool` no pai.
  -->
  <div class="std-toolbar">
    <div class="std-toolbar-group">
      <v-tooltip v-for="tool in visibleTools" :key="tool.tool" bottom>
        <template #activator="{ on }">
          <v-btn
            v-on="on"
            x-small
            depressed
            class="std-tool-btn"
            :color="activeTool === tool.tool ? primary : ''"
            :class="{ 'white--text': activeTool === tool.tool }"
            :disabled="readonly && tool.mutates"
            @click="$emit('set-tool', tool.tool)"
          >
            <v-icon size="18">{{ tool.icon }}</v-icon>
          </v-btn>
        </template>
        <span>{{ tool.label }}</span>
      </v-tooltip>

      <!-- inserir recorte vinculado (cuba/cooktop/furos): dropdown de símbolos -->
      <v-menu offset-y attach="[data-app]" content-class="std-toolbar-menu">
        <template #activator="{ on: menu }">
          <v-tooltip bottom>
            <template #activator="{ on: tooltip }">
              <v-btn
                v-on="{ ...tooltip, ...menu }"
                x-small
                depressed
                class="std-tool-btn std-tool-btn--wide"
                :color="isCutoutTool ? primary : ''"
                :class="{ 'white--text': isCutoutTool }"
                :disabled="readonly"
              >
                <v-icon size="18">mdi-inbox-outline</v-icon>
                <v-icon size="14">mdi-menu-down</v-icon>
              </v-btn>
            </template>
            <span>Inserir recorte (cuba, cooktop, furo...)</span>
          </v-tooltip>
        </template>
        <v-list dense>
          <v-list-item
            v-for="cut in cutoutOptions"
            :key="cut.key"
            @click="$emit('set-tool', `cutout:${cut.key}`)"
          >
            <v-icon size="16" class="mr-2">{{ cut.icon }}</v-icon>
            <v-list-item-title class="std-cutout-item">{{ cut.label }}</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>

      <!-- utilidades: parede (referência de encaixe, NÃO entra no orçamento) -->
      <v-menu offset-y attach="[data-app]" content-class="std-toolbar-menu">
        <template #activator="{ on: menu }">
          <v-tooltip bottom>
            <template #activator="{ on: tooltip }">
              <v-btn
                v-on="{ ...tooltip, ...menu }"
                x-small
                depressed
                class="std-tool-btn std-tool-btn--wide"
                :color="isUtilityTool ? primary : ''"
                :class="{ 'white--text': isUtilityTool }"
                :disabled="readonly"
              >
                <v-icon size="18">mdi-toolbox-outline</v-icon>
                <v-icon size="14">mdi-menu-down</v-icon>
              </v-btn>
            </template>
            <span>Utilidades (parede...) — não entram no orçamento</span>
          </v-tooltip>
        </template>
        <v-list dense>
          <v-list-item
            v-for="util in utilityOptions"
            :key="util.key"
            @click="$emit('set-tool', `utility:${util.key}`)"
          >
            <v-icon size="16" class="mr-2">{{ util.icon }}</v-icon>
            <v-list-item-title class="std-cutout-item">{{ util.label }}</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </div>

    <!-- undo / redo -->
    <div class="std-toolbar-group">
      <v-tooltip bottom>
        <template #activator="{ on }">
          <v-btn
            v-on="on"
            x-small
            depressed
            class="std-tool-btn"
            :disabled="readonly || !canUndo"
            @click="$emit('undo')"
          >
            <v-icon size="18">mdi-undo</v-icon>
          </v-btn>
        </template>
        <span>Desfazer (Ctrl+Z)</span>
      </v-tooltip>
      <v-tooltip bottom>
        <template #activator="{ on }">
          <v-btn
            v-on="on"
            x-small
            depressed
            class="std-tool-btn"
            :disabled="readonly || !canRedo"
            @click="$emit('redo')"
          >
            <v-icon size="18">mdi-redo</v-icon>
          </v-btn>
        </template>
        <span>Refazer (Ctrl+Shift+Z)</span>
      </v-tooltip>
    </div>

    <v-spacer></v-spacer>

    <!-- toggles de exibição -->
    <div class="std-toolbar-group">
      <v-btn
        x-small
        depressed
        class="std-tool-btn"
        :color="showGrid ? primary : ''"
        :class="{ 'white--text': showGrid }"
        @click="$emit('toggle-grid')"
      >
        <v-icon size="18">mdi-grid</v-icon>
      </v-btn>
      <v-btn
        x-small
        depressed
        class="std-tool-btn"
        :color="showMeasurements ? primary : ''"
        :class="{ 'white--text': showMeasurements }"
        @click="$emit('toggle-measurements')"
      >
        <v-icon size="18">mdi-ruler</v-icon>
      </v-btn>
      <!-- estilo "prancha técnica" (line-art preto-e-branco) -->
      <v-btn
        x-small
        depressed
        class="std-tool-btn"
        :color="technical ? primary : ''"
        :class="{ 'white--text': technical }"
        @click="$emit('toggle-style')"
      >
        <v-icon size="18">mdi-vector-square</v-icon>
      </v-btn>
      <!-- vista em perspectiva isométrica (somente leitura) -->
      <v-tooltip bottom>
        <template #activator="{ on }">
          <v-btn
            v-on="on"
            x-small
            depressed
            class="std-tool-btn"
            :color="isoView ? primary : ''"
            :class="{ 'white--text': isoView }"
            @click="$emit('toggle-iso')"
          >
            <v-icon size="18">mdi-video-3d</v-icon>
          </v-btn>
        </template>
        <span>Perspectiva (visualização)</span>
      </v-tooltip>
      <!-- painel de propriedades (ajustes finos): recolhido por padrão -->
      <v-tooltip bottom>
        <template #activator="{ on }">
          <v-btn
            v-on="on"
            x-small
            depressed
            class="std-tool-btn"
            :color="sideOpen ? primary : ''"
            :class="{ 'white--text': sideOpen }"
            @click="$emit('toggle-side')"
          >
            <v-icon size="18">mdi-tune</v-icon>
          </v-btn>
        </template>
        <span>Propriedades e ajustes finos</span>
      </v-tooltip>
    </div>

    <!-- viewport -->
    <div class="std-toolbar-group">
      <v-btn x-small depressed class="std-tool-btn" @click="$emit('zoom-in')">
        <v-icon size="18">mdi-magnify-plus-outline</v-icon>
      </v-btn>
      <v-btn x-small depressed class="std-tool-btn" @click="$emit('zoom-out')">
        <v-icon size="18">mdi-magnify-minus-outline</v-icon>
      </v-btn>
      <v-btn x-small depressed class="std-tool-btn" @click="$emit('center')">
        <v-icon size="18">mdi-image-filter-center-focus</v-icon>
      </v-btn>
      <v-btn x-small depressed class="std-tool-btn" @click="$emit('reset-view')">
        <v-icon size="18">mdi-backup-restore</v-icon>
      </v-btn>
    </div>
  </div>
</template>

<script>
export default {
  name: "DrawingToolbar",
  props: {
    activeTool: { type: String, default: "select" },
    showGrid: { type: Boolean, default: true },
    showMeasurements: { type: Boolean, default: true },
    technical: { type: Boolean, default: false },
    readonly: { type: Boolean, default: false },
    canUndo: { type: Boolean, default: false },
    canRedo: { type: Boolean, default: false },
    isoView: { type: Boolean, default: false },
    sideOpen: { type: Boolean, default: false },
    // opções do dropdown "Inserir recorte": vêm do ORQUESTRADOR
    // (insumos type "Furo" cadastrados, com fallback nos símbolos padrão)
    cutoutOptions: { type: Array, default: () => [] }
  },
  data() {
    return {
      // menu de UTILIDADES: itens de referência visual que NÃO entram no
      // orçamento (só ajudam a representar o ambiente/encaixe da peça).
      // Parede é o primeiro; novos itens entram aqui.
      utilityOptions: [
        { key: "wall", label: "Parede", icon: "mdi-wall" }
      ],
      // PONTO DE EXTENSÃO: novas ferramentas entram aqui.
      // `mutates` desabilita o botão em modo readonly.
      // Recortes (cuba/cooktop/furo) saíram daqui: agora são RECORTES
      // VINCULADOS à peça, inseridos pelo dropdown ao lado (cutout:<kind>).
      tools: [
        { tool: "select", label: "Selecionar", icon: "mdi-cursor-default-outline", mutates: false },
        { tool: "move", label: "Mover", icon: "mdi-cursor-move", mutates: true },
        { tool: "eraser", label: "Borracha — arraste sobre a peça para apagar/recortar", icon: "mdi-eraser", mutates: true },
        { tool: "annotation", label: "Adicionar anotação", icon: "mdi-format-text", mutates: true },
        { tool: "image", label: "Adicionar imagem", icon: "mdi-image-plus", mutates: true },
        { tool: "line", label: "Adicionar linha", icon: "mdi-vector-line", mutates: true },
        { tool: "arrow", label: "Adicionar seta", icon: "mdi-arrow-top-right", mutates: true },
        { tool: "seam", label: "Emenda", icon: "mdi-format-align-justify", mutates: true }
      ]
    };
  },
  computed: {
    visibleTools() {
      const hidden = new Set(["eraser", "line", "arrow", "seam"]);
      return this.tools.filter(tool => !hidden.has(tool.tool));
    },
    primary() {
      return (this.$theme && this.$theme.primary) || "#075e54";
    },
    isCutoutTool() {
      return `${this.activeTool || ""}`.startsWith("cutout:");
    },
    isUtilityTool() {
      return `${this.activeTool || ""}`.startsWith("utility:");
    }
  }
};
</script>

<style scoped>
.std-toolbar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  padding: 4px 2px;
}
.std-toolbar-group {
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 2px;
  border-radius: 10px;
  background: #f3f8f5;
  border: 1px solid rgba(7, 94, 84, 0.1);
}
.std-tool-btn {
  min-width: 30px !important;
  height: 30px !important;
  border-radius: 8px !important;
}
.std-tool-btn--wide {
  min-width: 44px !important;
}
.std-cutout-item {
  font-size: 12px !important;
}
</style>

<style>
.std-toolbar-menu {
  z-index: 2600 !important;
}
</style>
